import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet-velocity-ts';

/**
 * WindLayer — renders a velocity/particle animation over the Leaflet map.
 *
 * NOTE: The parent passes `key={windLineWidth}` so React fully unmounts and
 * remounts this component whenever lineWidth changes, guaranteeing a clean
 * canvas and an immediate visual update.
 */
const WindLayer = ({ data, colorScale }) => {
    const map = useMap();
    const layerRef = useRef(null);
    const timerRef = useRef(null);

    useEffect(() => {
        if (!map || !data) return;

        // Clear any queued init
        if (timerRef.current) clearTimeout(timerRef.current);

        // Small delay — lets the DOM settle after the key-remount before drawing
        timerRef.current = setTimeout(() => {
            // Remove any lingering layer (safety net)
            if (layerRef.current) {
                try { map.removeLayer(layerRef.current); } catch (_) {}
                layerRef.current = null;
            }

            try {
                const velocityLayer = L.velocityLayer({
                    displayValues: false,
                    data: data,
                    maxVelocity: 10,
                    velocityScale: 0.01,
                    particleAge: 90,
                    lineWidth: 1.5,
                    particleMultiplier: 1 / 80,
                    frameRate: 20,
                    colorScale: colorScale || [
                        '#4a90e2', // slow: deep blue
                        '#50e3c2', // teal
                        '#b8e986', // soft green
                        '#f8e71c', // yellow
                        '#f5a623', // orange
                        '#d0021b'  // fast: red
                    ],
                });

                // --- PATCH to prevent crash on resize / unmount ---
                const originalClearWind = velocityLayer.clearWind;
                if (typeof originalClearWind === 'function') {
                    velocityLayer.clearWind = function() {
                        try {
                            originalClearWind.apply(this, arguments);
                        } catch (e) {
                            console.warn('[WindLayer] Suppressed clearWind crash:', e.message);
                        }
                    };
                }
                // Also patch _clearWind if it exists
                if (typeof velocityLayer._clearWind === 'function') {
                    const original_ClearWind = velocityLayer._clearWind;
                    velocityLayer._clearWind = function() {
                        try {
                            original_ClearWind.apply(this, arguments);
                        } catch (e) {}
                    };
                }
                if (velocityLayer._windy && typeof velocityLayer._windy.stop === 'function') {
                    const originalStop = velocityLayer._windy.stop;
                    velocityLayer._windy.stop = function() {
                        try {
                            originalStop.apply(this, arguments);
                        } catch (e) {}
                    };
                }
                // --------------------------------------------------

                velocityLayer.addTo(map);
                layerRef.current = velocityLayer;
            } catch (err) {
                console.warn('[WindLayer] Could not create velocity layer:', err.message);
            }
        }, 200);

        // Cleanup on unmount (triggered by key change or showWind=false)
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            if (layerRef.current) {
                try { map.removeLayer(layerRef.current); } catch (_) {}
                layerRef.current = null;
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, data]); // colorScale baked in at mount; key={windColorScale} in parent forces remount


    return null;
};

export default WindLayer;
